import { Controller } from 'stimulus'

const POLL_INTERVAL_MS = 3_000
const SPINNER_AFTER_DOWNLOAD_MS = 1_500

// Waits for a certificate PDF to render, then starts the download.
// See CertificatePdfConcerns#respond_with_certificate_pdf, which this polls as JSON.
export default class extends Controller {
  static values = {
    statusUrl: String,
    status: String,
    pdfUrl: String,
    startedMessage: String,
  }
  static targets = ['preparing', 'message', 'failed', 'button', 'spinner']

  connect() {
    this.abortController = new AbortController()
    if (!this.hasPreparingTarget) return
    if (this.pdfUrlValue) this.startDownloadAfterLoad(this.pdfUrlValue)
    else if (this.statusValue !== 'failed') this.schedulePoll()
  }

  // Navigating to the PDF while the page is still loading stops the rest of the page from loading,
  // which can leave the member on a blank page.
  startDownloadAfterLoad(url) {
    if (document.readyState === 'complete') return this.startDownload(url)

    window.addEventListener('load', () => this.startDownload(url), {
      once: true,
      signal: this.abortController.signal,
    })
  }

  // Leaving the page (e.g. a Turbo visit) must not start a download or another poll later.
  disconnect() {
    clearTimeout(this.timeoutId)
    this.abortController.abort()
  }

  download(event) {
    event.preventDefault()
    if (this.busy) return

    this.downloadPageUrl = event.currentTarget.href
    this.setBusy(true)
    this.poll()
  }

  setBusy(busy) {
    this.busy = busy
    this.element.setAttribute('aria-busy', busy)
    this.buttonTarget.classList.toggle('hidden', busy)
    this.spinnerTarget.classList.toggle('hidden', !busy)
  }

  schedulePoll() {
    this.timeoutId = setTimeout(() => this.poll(), POLL_INTERVAL_MS)
  }

  async poll() {
    const { signal } = this.abortController
    let response
    try {
      response = await fetch(this.statusUrlValue, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal,
      })
    } catch {
      if (signal.aborted) return
      return this.schedulePoll() // Network blip; try again.
    }

    // Anything but our JSON (an error, or a login page after the session expired) can't be waited out.
    const data = response.ok ? await response.json().catch(() => null) : null
    if (signal.aborted) return
    if (!data) return this.showFailed()

    if (data.status === 'ready') {
      this.startDownloadAfterLoad(data.url)
    } else if (data.status === 'failed') {
      this.showFailed()
    } else {
      this.schedulePoll()
    }
  }

  startDownload(url) {
    if (this.hasMessageTarget)
      this.messageTarget.textContent = this.startedMessageValue
    else
      this.timeoutId = setTimeout(
        () => this.setBusy(false),
        SPINNER_AFTER_DOWNLOAD_MS
      )
    window.location.href = url
  }

  showFailed() {
    if (this.downloadPageUrl) {
      this.setBusy(false)
      window.location.href = this.downloadPageUrl
      return
    }

    this.statusValue = 'failed'
    this.preparingTarget.classList.add('hidden')
    this.failedTarget.classList.remove('hidden')
  }
}
